/**
 * Auth context — login, logout, who-am-I.
 * Tokens live in sessionStorage (cleared on tab close, simpler than refresh
 * rotation for P2; will add refresh logic in P6 hardening).
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiException } from "./api.js";

export interface User {
  id: string;
  email: string;
  role: "admin" | "trainer" | "trainer_pending" | "case";
  display_name?: string | null;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    if (!sessionStorage.getItem("cognihome_access_token")) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api<{ ok: true; user: User }>("/api/auth/me");
      setUser(r.user);
    } catch (e) {
      if (e instanceof ApiException && e.status === 401) {
        sessionStorage.removeItem("cognihome_access_token");
        sessionStorage.removeItem("cognihome_refresh_token");
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  // P7: react when api.ts forcibly clears tokens (refresh failed) so the
  // UI navigates to /login instead of staying with stale `user` state.
  useEffect(() => {
    const onCleared = () => setUser(null);
    window.addEventListener("cognihome:auth-cleared", onCleared);
    return () => window.removeEventListener("cognihome:auth-cleared", onCleared);
  }, []);

  async function login(email: string, password: string) {
    const r = await api<{
      ok: true;
      user: User;
      access_token: string;
      refresh_token: string;
    }>("/api/auth/login", { method: "POST", body: { email, password }, noAuth: true });
    sessionStorage.setItem("cognihome_access_token",  r.access_token);
    sessionStorage.setItem("cognihome_refresh_token", r.refresh_token);
    setUser(r.user);
    return r.user;
  }

  function logout() {
    sessionStorage.removeItem("cognihome_access_token");
    sessionStorage.removeItem("cognihome_refresh_token");
    setUser(null);
  }

  return (
    <Ctx.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
